import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Plug } from "lucide-react";
import { useConnectionsHistory } from '@features/cx/consumerManagement/hooks';

export interface consumerConnectionHistory {
  meterNumber: string;
  deviceNumber: string;
  utilityService: string;
  meterStatus: string;
  consumerNo: string;
  allocationDate: string;
  disconnectionDate: string | null;
  lastReading: number | null;
  lastReadingDate: string | null;
  firstReading: number;
  firstReadingDate: string;
}

interface ConnectionsHistoryProps {
  consumerNo: string;
  consumerID: string;
  remoteUtilityId: string;
}

export const ConnectionsHistory: React.FC<ConnectionsHistoryProps> = ({ 
  consumerNo, 
  consumerID, 
  remoteUtilityId 
}) => {
  const { 
    data: connections = [], 
    isLoading: loading, 
    error,
    refetch: refetchConnections
  } = useConnectionsHistory(consumerID, remoteUtilityId);

  const refetch = () => {
    refetchConnections();
  };

  if (error) {
    return (
      <div className="text-center py-8 text-red-600">
        <p>{error.message}</p>
        <button 
          onClick={() => refetch()}
          className="mt-2 px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
        >
          Retry
        </button>
      </div>
    );
  }

  // Group connections by utility service
  const groupedConnections: { [key: string]: consumerConnectionHistory[] } = 
    (Array.isArray(connections) ? connections : []).reduce((acc, connection) => {
      if (!acc[connection.utilityService]) {
        acc[connection.utilityService] = [];
      }
      acc[connection.utilityService].push(connection);
      return acc;
    }, {} as { [key: string]: consumerConnectionHistory[] });

  return (
    <div className="space-y-6">
      <Card className="bg-gradient-to-br to-indigo-50 border shadow-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plug className="h-5 w-5" />
            Utility Connections
          </CardTitle>
        </CardHeader>
        <CardContent>
          
            <div className="space-y-6">
              {Object.entries(groupedConnections).map(([utilityService, serviceConnections]) => (
                <UtilityServiceCard 
                  key={utilityService}
                  utilityService={utilityService}
                  connections={serviceConnections}
                />
              ))}
            </div>
         
        </CardContent>
      </Card>
    </div>
  );
};

interface UtilityServiceCardProps {
  utilityService: string;
  connections: consumerConnectionHistory[];
}

const UtilityServiceCard: React.FC<UtilityServiceCardProps> = ({ 
  utilityService, 
  connections 
}) => {
  return (
    <div className="p-5 rounded-lg bg-white shadow-sm border">
      <div className="flex items-center justify-between mb-4 pb-2 border-b">
        <h3 className="text-lg font-medium">{utilityService} Service</h3>
      </div>
      
      <div className="space-y-4">
        {connections.map((connection) => (
          <MeterCard 
            key={`${connection.meterNumber}-${connection.deviceNumber}`}
            connection={connection}
          />
        ))}
      </div>
    </div>
  );
};

interface MeterCardProps {
  connection: consumerConnectionHistory;
}

const MeterCard: React.FC<MeterCardProps> = ({ connection }) => {
  return (
    <div className="p-4 rounded-md border">
      <h4 className="font-medium mb-3 border-b pb-2 flex items-center">
        <svg className="h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 2v20M2 12h20M12 18a6 6 0 1 0 0-12 6 6 0 0 0 0 12z"/>
        </svg>
        Meter Details
      </h4>
      <MeterDetailsGrid connection={connection} />
    </div>
  );
};

interface MeterDetailsGridProps {
  connection: consumerConnectionHistory;
}

const MeterDetailsGrid: React.FC<MeterDetailsGridProps> = ({ connection }) => {
  const getStatusColor = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('active') || statusLower.includes('normal')) {
      return 'bg-green-500';
    } else if (statusLower.includes('inactive') || statusLower.includes('disconnected')) {
      return 'bg-red-500';
    } else if (statusLower.includes('pending')) {
      return 'bg-yellow-500';
    }
    return 'bg-gray-500';
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
      <div className="grid grid-cols-2 text-sm">
        <span className="font-medium">Meter No:</span>
        <span>{connection.meterNumber}</span>
      </div>
      <div className="grid grid-cols-2 text-sm">
        <span className="font-medium">Device No:</span>
        <span>{connection.deviceNumber}</span>
      </div>
      <div className="grid grid-cols-2 text-sm">
        <span className="font-medium">Consumer No:</span>
        <span>{connection.consumerNo}</span>
      </div>
      <div className="grid grid-cols-2 text-sm">
        <span className="font-medium">Allocation Date:</span>
        <span>{connection.allocationDate}</span>
      </div>
      <div className="grid grid-cols-2 text-sm">
        <span className="font-medium">Disconnection Date:</span>
        <span>{connection.disconnectionDate || 'N/A'}</span>
      </div>
      <div className="grid grid-cols-2 text-sm">
        <span className="font-medium">Meter Status:</span>
        <span className="flex items-center">
          <span className={`h-2 w-2 mr-1.5 rounded-full ${getStatusColor(connection.meterStatus)}`}></span>
          {connection.meterStatus}
        </span>
      </div>
      <div className="grid grid-cols-2 text-sm">
        <span className="font-medium">First Reading:</span>
        <span>{connection.firstReading} ({connection.firstReadingDate})</span>
      </div>
      <div className="grid grid-cols-2 text-sm">
        <span className="font-medium">Last Reading:</span>
        <span>
          {connection.lastReading !== null && connection.lastReadingDate 
            ? `${connection.lastReading} (${connection.lastReadingDate})`
            : 'N/A'
          }
        </span>
      </div>
    </div>
  );
};